<template>
  <div
    v-if="!isNativeShell"
    class="gnb-navigation-bar flex flex-space-between flex-align-center"
    :class="{ 'navigation-bar-ios-safe-area': isIos && !isApp }"
  >
    <NavigationButton
      id="gtm_bnb_home_btn"
      :is-partner="isPartner"
      :route-url="routeUrl.home"
      :current-path="currentPath"
      :activate-trigger="currentPath === routeUrl.home"
      :button-text="'홈'"
      icon="i-usdg-navigation-home"
    />
    <NavigationButton
      id="gtm_bnb_mydata_btn"
      :is-partner="isPartner"
      :route-url="routeUrl.inventory"
      :current-path="currentPath"
      :activate-trigger="currentPath === routeUrl.inventory"
      :button-text="'내 데이터'"
      icon="i-usdg-navigation-chart-bar"
    />
    <NavigationButton
      v-if="!partnersKey"
      id="gtm_bnb_benefit_btn"
      :route-url="routeUrl.benefit"
      :current-path="currentPath"
      :activate-trigger="currentPath === routeUrl.benefit"
      :button-text="'혜택'"
      icon="i-usdg-navigation-gift"
    />
    <button
      v-if="isShowCallBtn"
      id="gtm_bnb_call_btn"
      class="call-button"
      @click="openCallInfo()"
    >
      <nuxt-img
        src="/icons-v2/icon-call-off.svg"
        class="icon"
        width="24"
        height="24"
        alt="통화 아이콘"
      />
      <p>전화</p>
    </button>
    <NavigationButton
      id="gtm_bnb_profile_btn"
      :is-partner="isPartner"
      :route-url="routeUrl.user"
      :current-path="currentPath"
      :activate-trigger="currentPath.includes(routeUrl.user)"
      :button-text="'마이'"
      icon="i-usdg-navigation-person"
    />
    <NavigationButton
      v-if="partnersKey"
      id="gtm_bnb_settings_btn"
      :is-partner="isPartner"
      :route-url="routeUrl.setting"
      :current-path="currentPath"
      :activate-trigger="currentPath === routeUrl.setting"
      :button-text="'더보기'"
      icon="i-solid-dots-horizontal"
    />
    <CallInfoDialog
      :is-call-info-dialog="isCallInfoDialog"
      @close="isCallInfoDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import type { GnbRouteUrl } from '@/types/layouts/navigation'
import { isNativeShellAppUA } from '@/utils/appBridgeGate'
const { showChannelTalk, hideChannelTalk } = useChannelTalk()

const { isIos } = useDevice()
const { isApp, userAgent, isCallSupportedAppVersion } = useAppCheck()

// 2.2.6 이하는 웹 GNB 노출.
const isNativeShell = computed(() => isApp && isNativeShellAppUA(userAgent))

const { isLoginStatus, partnersKey } = storeToRefs(useAuthStore())

const props = defineProps<{
  routeUrl: GnbRouteUrl
}>()

const currentPath = computed(() => {
  return useRoute().path
})

const isPartner = computed(() => {
  return !!partnersKey.value
})

const isCallInfoDialog = ref<boolean>(false)

const route = useRoute()

// 외부 sdk이기 때문에 모바일에서 항상 가려짐 때문에 보일때에 다른 분기처리
watch(isCallInfoDialog, (newDialog) => {
  if (route.fullPath !== '/') return

  if (newDialog) {
    hideChannelTalk()
  } else {
    showChannelTalk()
  }
})

const isCallAuthStatus = useCookie<boolean>('isCallAuthStatus')
const openCallInfo = () => {
  if (!isApp) {
    isCallInfoDialog.value = true
    return
  } else {
    window.flutter_inappwebview?.callHandler('openCallInfo')
    return
  }
}

onMounted(() => {
  if (isCallAuthStatus.value && route.fullPath === '/user') {
    openCallInfo()
    isCallAuthStatus.value = false
  }
})

const isShowCallBtn = computed(() => {
  if (partnersKey.value) {
    return false
  }
  return true
})
</script>

<style lang="scss" scoped>
.gnb-navigation-bar {
  position: fixed;
  width: 100%;
  max-width: 500px;
  z-index: 101;
  bottom: 0;
  height: 64px;
  border-top: 0.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 18px 18px 0 0;
  background-color: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  .call-button {
    width: 100%;
    height: 100%;
    padding: 11px 0;
    background: none;
    border: none;
    margin: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #bdbdbd;
    &:active {
      color: #212121;
    }
    .icon {
      width: 24px;
      height: 24px;
      margin-bottom: 4px;
    }
    p {
      @include footnote2-medium;
    }
  }
}
.navigation-bar-ios-safe-area {
  align-items: unset;
  height: unset !important;
  padding: 0 0 max(env(safe-area-inset-bottom), 0px);
}
</style>
